/**
 * The checkout half of the drop-in.
 *
 * A merchant can run this same checkout inside their own page by loading
 * `distrxpay.js` and calling `distrxpay.open(...)`. That script puts this page in
 * an iframe; these functions are how the page talks back out of it.
 *
 * There is one rule that makes this safe to ship: we post to exactly one origin,
 * the one the opener declared, and we never post anything a customer could not
 * already read on this page. No token, no card, no API key — a payment id and a
 * status, which is what the merchant's own webhook will tell them anyway.
 *
 * The merchant's page is not trusted to be honest about who it is, so the claim
 * is checked against `document.referrer`. A page that frames us and lies about
 * its origin gets no messages; it also learns nothing, since it had to know the
 * session id to frame us at all.
 */
const CHANNEL = 'distrxpay';

/** A well-formed https origin, or http on a development host. */
function parseOrigin(value) {
  try {
    const url = new URL(value);
    if (url.origin !== value) return null;
    const local = /^(localhost|127\.0\.0\.1|\[::1\])$/i.test(url.hostname);
    if (url.protocol !== 'https:' && !(url.protocol === 'http:' && local)) return null;
    return url.origin;
  } catch {
    return null;
  }
}

/**
 * The origin to talk to, or null when this page is not embedded.
 *
 * Resolved once: a later `history.replaceState` must not change who we are
 * talking to half way through a payment.
 */
const target = (() => {
  if (typeof window === 'undefined' || window.parent === window) return null;

  const claimed = parseOrigin(new URLSearchParams(window.location.search).get('embed') ?? '');
  if (!claimed) return null;

  // An empty referrer means a referrer policy stripped it, which is a legitimate
  // setup — the claim stands alone there. A referrer that is *present and
  // different* is a page framing us under someone else's name.
  if (document.referrer) {
    const referrer = parseOrigin(new URL(document.referrer).origin);
    if (referrer && referrer !== claimed) return null;
  }

  return claimed;
})();

export const isEmbedded = () => target !== null;

function post(message) {
  if (!target) return;
  try {
    window.parent.postMessage({ source: CHANNEL, ...message }, target);
  } catch {
    /* a closed or cross-origin-restricted opener is not this page's problem */
  }
}

/** Sent once the checkout has rendered, so the opener can drop its spinner. */
export const postReady = () => post({ type: 'ready' });

/**
 * Sent when the payment reaches a final state.
 *
 * HC-7: brand and last four are the most we ever hold, and neither is sent here.
 */
export function postOutcome(outcome) {
  post({
    type: 'outcome',
    result: outcome?.result ?? null,
    paymentId: outcome?.payment?.id ?? null,
    orderId: outcome?.payment?.orderId ?? null,
    status: outcome?.payment?.status ?? null,
  });
}

/** Sent when the customer abandons the checkout from inside the frame. */
export const postClose = () => post({ type: 'close' });

export default { isEmbedded, postReady, postOutcome, postClose };
